import { Container } from 'unstated';
import * as voicifyApi from '../../api';
import OrganizationModel from '../../models/organizations/api/OrganizationModel';
import IResult from '../../models/result/IResult';
import UserRoleModel from '../../models/user/UserRoleModel';
import UpdateOrganizationRequest from '../../models/organizations/api/UpdateOrganizationRequest';
import _ from 'lodash';

type OrganizationState = {
    organizations: OrganizationModel[]
    currentOrganization: OrganizationModel
    isLoadingOrgs: boolean
    disabledOrganizationSearchResults: OrganizationModel[]
    isLoadingDisabledOrganizations: boolean
    isUpdatingDisabledState: boolean
    isLoadingRoles: boolean
    isUpdating: boolean
    systemRoles: UserRoleModel[]
    errors: string[]
}

export default class OrganizationContainer extends Container<OrganizationState> {
    private disabledOrganizationSearchRequestId = 0;

    // default state
    state = this.getInitialState()

    getInitialState(): OrganizationState {
        // check local storage for initial state
        const storedOrganization = localStorage.getItem("CURRENT_ORG");
        
        if (!!storedOrganization && storedOrganization !== "undefined") {
            return {
                organizations: [],
                currentOrganization: JSON.parse(storedOrganization),
                isLoadingOrgs: false,
                disabledOrganizationSearchResults: [],
                isLoadingDisabledOrganizations: false,
                isUpdatingDisabledState: false,
                isLoadingRoles: false,
                isUpdating: false,
                systemRoles: [],
                errors: []
            }
        }

        return {
            organizations: [],
            currentOrganization: null,
            isLoadingOrgs: false,
            disabledOrganizationSearchResults: [],
            isLoadingDisabledOrganizations: false,
            isUpdatingDisabledState: false,
            isLoadingRoles: false,
            isUpdating: false,
            systemRoles: [],
            errors: []
        }
    }

    getSystemRoles(): Promise<IResult<UserRoleModel[]>> {
        if (this.state.systemRoles.length > 0) {
            return new Promise(resolve => resolve({
                resultType: "Ok",
                errors: [],
                data: this.state.systemRoles
            }))
        }
        this.setState({
            ...this.state,
            isLoadingRoles: true
        })
        var promise = voicifyApi.getSystemUserRoles();
        promise.then(result => {
            if (result.resultType == "Ok") {
                this.setState({
                    ...this.state,
                    isLoadingRoles: false,
                    systemRoles: result.data
                })
            }
            else {
                this.setState({
                    ...this.state,
                    isLoadingRoles: false
                })
            }
        }).catch(error => {
            this.setState({
                ...this.state,
                isLoadingRoles: false
            })
        })

        return promise;
    }

    async setCurrentOrgById(id: string) {
        if (id === null) {
            await this.setCurrentOrganization(null);
            return;
        }

        let matchingOrgs = this.state.organizations.filter(o => o.id === id);
        if (matchingOrgs.length < 1) {
            try {
                const orgs = await this.getOrganizations();
                matchingOrgs = [orgs.data.find(o => o.id === id)];
            } catch (error) {
                console.log(error);
                return;
            }
        }

        await this.setCurrentOrganization(matchingOrgs[0]);
    }

    async setCurrentOrganization(organization: OrganizationModel) {
        if (organization) {
            localStorage.setItem("CURRENT_ORG", JSON.stringify(organization));
        } else {
            localStorage.removeItem("CURRENT_ORG");
        }

        await this.setState({
            ...this.state,
            currentOrganization: organization,
            isUpdating: false
        });
    }

    async searchDisabledOrganizations(search: string): Promise<IResult<OrganizationModel[]>> {
        const normalizedSearch = search?.trim() ?? '';
        const requestId = ++this.disabledOrganizationSearchRequestId;
        if (normalizedSearch.length < 2) {
            const result: IResult<OrganizationModel[]> = {
                resultType: "Invalid",
                errors: ["Enter at least two characters to search disabled organizations."],
                data: []
            };
            await this.setState({
                ...this.state,
                disabledOrganizationSearchResults: [],
                isLoadingDisabledOrganizations: false,
                errors: []
            });
            return result;
        }

        await this.setState({
            ...this.state,
            disabledOrganizationSearchResults: [],
            isLoadingDisabledOrganizations: true,
            errors: []
        });

        const result = await voicifyApi.searchDisabledOrganizations(normalizedSearch);
        if (requestId !== this.disabledOrganizationSearchRequestId) {
            return result;
        }
        if (result.resultType === "Ok") {
            await this.setState({
                ...this.state,
                disabledOrganizationSearchResults: result.data,
                isLoadingDisabledOrganizations: false,
                errors: []
            });
        } else {
            await this.setState({
                ...this.state,
                disabledOrganizationSearchResults: [],
                isLoadingDisabledOrganizations: false,
                errors: result.errors
            });
        }

        return result;
    }

    async updateCurrentOrganizationDisabled(isDisabled: boolean): Promise<IResult<OrganizationModel>> {
        if (!this.state.currentOrganization) {
            return {
                resultType: "Invalid",
                errors: ["No current organization"],
                data: null
            };
        }

        await this.setState({
            ...this.state,
            isUpdatingDisabledState: true,
            errors: []
        });

        const result = await voicifyApi.updateOrganizationDisabled(this.state.currentOrganization.id, isDisabled);
        if (result.resultType === "Ok") {
            const organizations = isDisabled
                ? this.state.organizations.filter(organization => organization.id !== result.data.id)
                : _.sortBy([
                    ...this.state.organizations.filter(organization => organization.id !== result.data.id),
                    result.data
                ], organization => organization.name);

            localStorage.setItem("CURRENT_ORG", JSON.stringify(result.data));
            await this.setState({
                ...this.state,
                organizations,
                currentOrganization: result.data,
                disabledOrganizationSearchResults: this.state.disabledOrganizationSearchResults
                    .filter(organization => organization.id !== result.data.id),
                isUpdatingDisabledState: false,
                errors: []
            });
        } else {
            await this.setState({
                ...this.state,
                isUpdatingDisabledState: false,
                errors: result.errors
            });
        }

        return result;
    }

    clearDisabledOrganizationSearch() {
        this.disabledOrganizationSearchRequestId++;
        return this.setState({
            ...this.state,
            disabledOrganizationSearchResults: [],
            isLoadingDisabledOrganizations: false,
            errors: []
        });
    }

    async getOrganizations(): Promise<IResult<OrganizationModel[]>> {

        this.setLoading(true);

        const organizationsResult = await voicifyApi.getOrganizations();

        if (organizationsResult.resultType == "Ok") {
            await this.setState({
                ...this.state,
                errors: [],
                organizations: _.sortBy(organizationsResult.data, o => o.name),
                isLoadingOrgs: false
            });
            // check the loaded orgs. If we have just one, set it to current
            const currentOrganizationIsDisabled = this.state.currentOrganization?.isDisabled === true;
            if (organizationsResult.data.length === 1 && !currentOrganizationIsDisabled) {
                await this.setCurrentOrgById(organizationsResult.data[0].id);
            }
        }
        else {
            await this.setState({
                ...this.state,
                errors: organizationsResult.errors,
                isLoadingOrgs: false
            })
        }

        return organizationsResult;
    
    };

    updateOrganizationInfo(model: UpdateOrganizationRequest) {
        if (this.state.currentOrganization) {
            this.setState({
                ...this.state,
                isUpdating: true
            });
            const promise = voicifyApi.updateOrganizationInfo(this.state.currentOrganization.id, model).then(result => {
                if (result.resultType == "Ok") {
                    // update orgs list
                    var orgs = this.state.organizations;
                    if (orgs) {
                        var currentOrgIndex = this.state.organizations.findIndex(o => o.id == result.data.id);
                        if (currentOrgIndex > -1) {
                            orgs[currentOrgIndex] = result.data;
                            this.setState({
                                ...this.state,
                                organizations: orgs,
                                isUpdating: false,
                                errors: []
                            })
                        }
                    }

                    // update current org
                    if (this.state.currentOrganization.id == result.data.id) {
                        this.setCurrentOrgById(result.data.id)
                    }
                }
                else {
                    this.setState({
                        ...this.state,
                        isUpdating: false,
                        errors: result.errors
                    })
                }
            }).catch(error => {
                this.setState({
                    ...this.state,
                    isUpdating: false,
                    errors: [error]
                })
            });


            return promise;
        }
    }

    updateOrganizationClientContext(clientContext: string): Promise<IResult<OrganizationModel>> {
        if (!this.state.currentOrganization) {
            return Promise.resolve({
                resultType: "Invalid",
                errors: ["No current organization"],
                data: null
            });
        }

        this.setState({
            ...this.state,
            isUpdating: true
        });

        const promise = voicifyApi.updateOrganizationClientContext(this.state.currentOrganization.id, clientContext).then(result => {
            if (result.resultType == "Ok") {
                const organizations = this.state.organizations?.map(org =>
                    org.id == result.data.id ? { ...org, ...result.data } : org
                );
                const currentOrganization = this.state.currentOrganization?.id == result.data.id
                    ? { ...this.state.currentOrganization, ...result.data }
                    : this.state.currentOrganization;

                localStorage.setItem("CURRENT_ORG", JSON.stringify(currentOrganization));
                this.setState({
                    ...this.state,
                    organizations,
                    currentOrganization,
                    isUpdating: false,
                    errors: []
                });
            }
            else {
                this.setState({
                    ...this.state,
                    isUpdating: false,
                    errors: result.errors
                });
            }

            return result;
        }).catch(error => {
            this.setState({
                ...this.state,
                isUpdating: false,
                errors: [error]
            });

            throw error;
        });

        return promise;
    }

    clearErrors() {
        this.setState({
            ...this.state,
            errors: []
        })
    };


    private setLoading(isLoading: boolean) {
        this.setState({
            ...this.state,
            isLoadingOrgs: isLoading
        });
    }
}
